import { useState } from 'react';
import { toast } from 'react-toastify';
import { FaTrash } from 'react-icons/fa6';
import { FiDownload, FiEye, FiFileText } from 'react-icons/fi';
import { formatDate } from '../../../../utils/collateralFormatters';
import { FileManager } from '../../../file-manager';
import useLegalCaseDocuments, { openDocument } from '../../../../hooks/useLegalCaseDocuments';
import type { LegalCaseDocument } from '../../../../types/LegalCase';
import ConfirmDialog from './ConfirmDialog';

interface LegalCaseDocumentsProps {
    caseId: string;
    initialDocuments?: LegalCaseDocument[];
}

// Uma linha de anexo: nome + data, com ações de visualizar (inline), baixar (attachment) e
// remover. Extraída para fora do componente (diretriz: sem função dentro de função).
interface DocumentRowProps {
    doc: LegalCaseDocument;
    onView: () => void;
    onDownload: () => void;
    onRemove: () => void;
    disabled: boolean;
}

const DocumentRow = ({ doc, onView, onDownload, onRemove, disabled }: DocumentRowProps) => (
    <li className="flex items-center justify-between gap-2 rounded-lg bg-gray-50 dark:bg-slate-900 px-3 py-2">
        <div className="flex items-center gap-2 min-w-0">
            <FiFileText className="text-gray-400 shrink-0" size={15} />
            <div className="min-w-0">
                <p className="text-sm dark:text-white truncate">{doc.fileName}</p>
                {doc.description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{doc.description}</p>
                )}
            </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
            {doc.createdAt && (
                <span className="text-xs text-gray-400 dark:text-gray-500 mr-1">
                    {formatDate(doc.createdAt)}
                </span>
            )}
            <button
                onClick={onView}
                title="Visualizar"
                className="p-1.5 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-lg"
            >
                <FiEye size={13} />
            </button>
            <button
                onClick={onDownload}
                title="Baixar"
                className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900 rounded-lg"
            >
                <FiDownload size={13} />
            </button>
            <button
                onClick={onRemove}
                disabled={disabled}
                title="Remover anexo"
                className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900 rounded-lg disabled:opacity-50"
            >
                <FaTrash size={11} />
            </button>
        </div>
    </li>
);

// Seção Documentos do detalhe do caso (Task 13). O upload é do FileManager (frontend→barn,
// space "recovery"); ao concluir, vinculamos o fileId ao caso via addDocument. Baixar e
// visualizar pedem a URL assinada ao barn (openDocument). A remoção confirma antes.
const LegalCaseDocuments = ({ caseId, initialDocuments }: LegalCaseDocumentsProps) => {
    const { documents, isSaving, addDocument, removeDocument } = useLegalCaseDocuments(
        caseId,
        initialDocuments,
    );
    const [pendingRemoval, setPendingRemoval] = useState<string | null>(null);

    const confirmRemoval = async () => {
        if (!pendingRemoval) return;
        await removeDocument(pendingRemoval);
        setPendingRemoval(null);
    };

    // Vincula o arquivo (já no barn) ao caso. Se o POST falhar, avisa com opção de tentar de
    // novo — sem retry silencioso, o arquivo ficaria órfão no barn e o usuário não saberia.
    const onAttach = async (fileId: string, file: File) => {
        const ok = await addDocument({
            barnFileId: fileId,
            fileName: file.name,
            contentType: file.type || undefined,
        });
        if (!ok) {
            toast.error(`Falha ao anexar "${file.name}". Tente anexar novamente.`, {
                onClick: () => onAttach(fileId, file),
            });
        }
    };

    return (
        <div className="space-y-4">
            {/* clearOnComplete: ao concluir o upload, o arquivo vira anexo do caso (addDocument)
                e some da fila do FileManager — some assim a duplicação e o risco de a lixeira do
                FileManager apagar do barn um objeto já referenciado pelo anexo (→ 404). O único
                lugar de remover um anexo consolidado é a lista abaixo (desvincula). */}
            <FileManager
                spaceName="recovery"
                label="Anexar documento"
                metadata={{ legalCaseId: caseId }}
                confirmRemoval
                clearOnComplete
                onUploadComplete={(fileId, file) => {
                    // O upload no barn já concluiu; agora vincula ao caso. Se o vínculo falhar,
                    // o arquivo fica no barn sem documento no caso — o addDocument devolve false
                    // e mostra um toast com ação de tentar de novo, para o usuário não sair da
                    // tela achando que anexou (apontamento do Copilot).
                    onAttach(fileId, file);
                }}
            />

            <div>
                <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">
                    Documentos do caso
                </h4>
                {documents.length === 0 ? (
                    <p className="text-sm text-gray-400 dark:text-gray-500">
                        Nenhum documento anexado.
                    </p>
                ) : (
                    <ul className="space-y-2">
                        {documents.map((doc) => (
                            <DocumentRow
                                key={doc.id}
                                doc={doc}
                                onView={() => openDocument(doc.barnFileId, 'inline')}
                                onDownload={() => openDocument(doc.barnFileId, 'attachment')}
                                onRemove={() => setPendingRemoval(doc.id)}
                                disabled={isSaving}
                            />
                        ))}
                    </ul>
                )}
            </div>

            <ConfirmDialog
                open={pendingRemoval !== null}
                title="Remover anexo"
                message="Remover este anexo do caso? Esta ação não pode ser desfeita."
                isBusy={isSaving}
                onConfirm={confirmRemoval}
                onCancel={() => setPendingRemoval(null)}
            />
        </div>
    );
};

export default LegalCaseDocuments;
